import clsx from 'clsx';
import Slider from 'rc-slider';
import Tooltip from 'rc-tooltip';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './CustomRange.module.scss';
import { FilterFacet } from '../filter/Filter';
import { CustomRangeProps } from './types';

const CustomRange: React.FC<CustomRangeProps> = ({ facet, onPriceRangeChange, className = '', showDollar = true }) => {
  const { min, max } = facet;

  const defaultValue: [number, number] =
    facet?.value?.min !== 0 && facet?.value?.max !== 0
      ? [parseInt(facet.value.min.toString(), 10), parseInt(facet.value.max.toString(), 10)]
      : [min, max];
  const [value, setValue] = useState<[number, number]>(defaultValue);
  const [leftInputValue, setLeftInputValue] = useState<number>(Math.ceil(defaultValue[0] / 100));
  const [rightInputValue, setRightInputValue] = useState<number>(Math.ceil(defaultValue[1] / 100));
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);

  const sliderMax = facet.max;
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setTooltipVisible(true);
    }, 175);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (!facet.selected) {
      setValue([facet.min, facet.max]);
      setLeftInputValue(Math.ceil(facet.min / 100));
      setRightInputValue(Math.ceil(facet.max / 100));
    }
  }, [facet.selected, facet.max, facet.min]);

  const formatter = useMemo(
    () =>
      (value: number): string =>
        `${showDollar && '$'}${Math.ceil(parseInt(value.toString(), 10) / 100)}`,
    [],
  );

  const applyRange = (facet: FilterFacet, values: [number, number]): void => {
    const [min, max] = values;
    onPriceRangeChange(facet, min, max);
  };

  const handleChange = useCallback(
    (newValues: number[]) => {
      const minSliderContainer = document.querySelector('.slider-tooltip-min');
      const maxSliderContainer = document.querySelector('.slider-tooltip-max');
      const minContainer = minSliderContainer?.parentElement?.parentElement;
      const maxContainer = maxSliderContainer?.parentElement?.parentElement;
      const newValue: [number, number] = [newValues[0] * 100, newValues[1] * 100];

      if (minContainer && maxContainer) {
        const minLeft = (newValue[0] / max) * 100;
        const maxLeft = (newValue[1] / max) * 100;

        minContainer.style.left = `calc(${minLeft}% + 5px)`;
        maxContainer.style.left = `${maxLeft}%`;
      }
      setValue(newValue);
      setLeftInputValue(newValues[0]);
      setRightInputValue(newValues[1]);
      applyRange(facet, newValue);
    },
    [facet, max],
  );

  return (
    <>
      <div className={clsx(styles.customRange, className)}>
        <Slider
          range
          min={0}
          max={sliderMax}
          value={value}
          step={0.01}
          onChange={(values) => {
            if (!Array.isArray(values)) {
              return;
            }
            const newValues = values.map((val) => Math.ceil(Number(val) / 100)) as number[];
            handleChange(newValues);
          }}
          allowCross={false}
        />
        <div className={styles.rangeContainer}>
          <Tooltip
            id="rc-slider-tooltip-min"
            prefixCls="rc-slider-tooltip"
            overlay={<span className={styles.customRangeSum}>${leftInputValue}</span>}
            // @ts-ignore
            classNames={{ root: 'slider-tooltip-min' }}
            visible={tooltipVisible}
            defaultVisible={false}
            showArrow={false}
            placement="top"
            align={{
              offset: [0, -30],
            }}
            getTooltipContainer={(node: HTMLElement) => node}
          >
            <span
              style={{
                position: 'absolute',
                left: `calc(${(value[0] / sliderMax) * 100}%)`,
                bottom: '-25px',
              }}
            />
          </Tooltip>
          <Tooltip
            id="rc-slider-tooltip-max"
            prefixCls="rc-slider-tooltip"
            overlay={<span className={styles.customRangeSum}>${rightInputValue}</span>}
            // @ts-ignore
            classNames={{ root: 'slider-tooltip-max' }}
            visible={tooltipVisible}
            defaultVisible={false}
            showArrow={false}
            placement="top"
            align={{
              offset: [0, -30],
            }}
            getTooltipContainer={(node: HTMLElement) => node}
          >
            <span
              style={{
                position: 'absolute',
                left: `calc(${(value[1] / max) * 100}%)`,
                bottom: '-25px',
              }}
            />
          </Tooltip>
        </div>
      </div>
      <div className={styles.customRangeSum}>
        {formatter(min) ?? ''} - {formatter(max) ?? ''}
      </div>
    </>
  );
};

export default CustomRange;
